import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvalMsg, setApprovalMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data || []);
    } catch {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApproval = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/approval`, { approvalStatus: status, approvalMessage: approvalMsg });
      toast.success(status === 'approved' ? 'Entregador aprovado!' : 'Entregador reprovado');
      setSelectedUser(null);
      setApprovalMsg('');
      load();
    } catch {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('Usuário excluído');
      load();
    } catch {
      toast.error('Erro ao excluir usuário');
    }
  };

  const deliveryUsers = users.filter(u => u.role === 'delivery');
  const customerUsers = users.filter(u => u.role === 'customer');

  const UserCard = ({ u }) => (
    <div key={u.id} className="bg-white rounded-2xl p-4 shadow-sm mb-3">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${u.role === 'delivery' ? 'bg-orange-100' : 'bg-blue-100'}`}>
          {u.role === 'delivery' ? '🛵' : '🛒'}
        </div>
        <div className="flex-1">
          <div className="font-bold text-gray-800 text-sm">{u.name || u.email}</div>
          <div className="text-xs text-gray-400">{u.email}</div>
        </div>
        <button onClick={() => handleDelete(u.id)} className="text-red-500 text-xs font-bold px-2 py-1 rounded border border-red-200">Excluir</button>
      </div>

      {u.role === 'delivery' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.approval_status === 'approved' ? 'bg-green-100 text-green-700' : u.approval_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {u.approval_status === 'approved' ? '✅ Aprovado' : u.approval_status === 'rejected' ? '❌ Reprovado' : '⏳ Pendente'}
            </span>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 space-y-1.5 text-xs">
            <div><span className="font-semibold text-gray-600">Celular:</span> {u.phone || '—'}</div>
            <div><span className="font-semibold text-gray-600">PIX (CPF):</span> {u.pix_key_cpf || '—'}</div>
            <div><span className="font-semibold text-gray-600">Código ANP:</span> {u.anp_code || '—'}</div>
            <div><span className="font-semibold text-gray-600">Placa:</span> {u.vehicle_plate || '—'}</div>
            <div><span className="font-semibold text-gray-600">Modelo:</span> {u.vehicle_model || '—'}</div>
            <div><span className="font-semibold text-gray-600">Marcas:</span> {(u.brands || []).join(', ') || '—'}</div>
            <div><span className="font-semibold text-gray-600">Aceitou termos:</span> {u.accepted_terms ? 'Sim' : 'Não'}</div>
            {u.approval_message && <div><span className="font-semibold text-gray-600">Mensagem:</span> {u.approval_message}</div>}
          </div>

          {u.cnh_front_image && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-1">CNH Frente</div>
                <img src={u.cnh_front_image} alt="CNH Frente" className="w-full h-24 object-cover rounded-xl border" />
              </div>
              {u.cnh_back_image && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 mb-1">CNH Verso</div>
                  <img src={u.cnh_back_image} alt="CNH Verso" className="w-full h-24 object-cover rounded-xl border" />
                </div>
              )}
            </div>
          )}

          {u.approval_status === 'pending' && (
            <div className="pt-2">
              <textarea
                value={approvalMsg}
                onChange={e => setApprovalMsg(e.target.value)}
                placeholder="Mensagem de aprovação/reprovação (opcional)"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs mb-2 resize-none"
                rows={2}
              />
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => handleApproval(u.id, 'rejected')} className="py-2 bg-red-100 text-red-700 rounded-xl font-bold text-xs">Reprovar</button>
                <button onClick={() => handleApproval(u.id, 'approved')} className="py-2 bg-green-500 text-white rounded-xl font-bold text-xs">Aprovar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="screen bg-gray-50 flex flex-col">
      <div className="gradient-hero px-6 pt-10 pb-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex justify-center mb-3">
            <img src="/logo.png" alt="GasNaPorta" className="h-20 w-auto object-contain logo-glow" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-black">Administrativo</span>
              <h1 className="text-base font-black mt-0.5">Usuários e Aprovações</h1>
            </div>
            <div className="flex gap-2">
              <button onClick={() => nav('/admin')} className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full font-bold">← Voltar</button>
              <button onClick={logout} className="text-sky-200 hover:text-white text-sm">Sair</button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading && <p className="text-center text-gray-400 py-8">Carregando...</p>}

        {/* Entregadores Pendentes */}
        {deliveryUsers.filter(u => u.approval_status === 'pending').length > 0 && (
          <>
            <h2 className="font-black text-gray-800 text-sm mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500" />
              Entregadores Pendentes ({deliveryUsers.filter(u => u.approval_status === 'pending').length})
            </h2>
            {deliveryUsers.filter(u => u.approval_status === 'pending').map(u => <UserCard key={u.id} u={u} />)}
          </>
        )}

        {/* Entregadores Aprovados */}
        {deliveryUsers.filter(u => u.approval_status === 'approved').length > 0 && (
          <>
            <h2 className="font-black text-gray-800 text-sm mb-3 flex items-center gap-2 mt-4">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              Entregadores Aprovados ({deliveryUsers.filter(u => u.approval_status === 'approved').length})
            </h2>
            {deliveryUsers.filter(u => u.approval_status === 'approved').map(u => <UserCard key={u.id} u={u} />)}
          </>
        )}

        {/* Clientes */}
        {customerUsers.length > 0 && (
          <>
            <h2 className="font-black text-gray-800 text-sm mb-3 flex items-center gap-2 mt-4">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              Clientes ({customerUsers.length})
            </h2>
            {customerUsers.map(u => (
              <div key={u.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-xl">🛒</div>
                <div className="flex-1">
                  <div className="font-bold text-gray-800 text-sm">{u.name || u.email}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                </div>
                <button onClick={() => handleDelete(u.id)} className="text-red-500 text-xs font-bold px-2 py-1 rounded border border-red-200">Excluir</button>
              </div>
            ))}
          </>
        )}

        {users.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-2">👤</p>
            <p>Nenhum usuário cadastrado</p>
          </div>
        )}
      </div>
    </div>
  );
}
